import ExcelJS from 'exceljs';
import { formatDate } from 'lib/functions/utils';

interface MajorBookLine {
  date: string;
  businessIdentifier: string;
  businessName: string;
  account?: string;
  accountName?: string;
  description?: string;
  entryId?: string;
  className?: string;
  documentId?: string;
  debit?: number;
  credit?: number;
  debitFinal?: number;
  creditFinal?: number;
  [key: string]: unknown;
}

interface MajorBookCategory {
  groupedLines: MajorBookLine[];
}

interface MajorBookResponse {
  majorBookAccounts: MajorBookCategory[];
  groupedByCategoryAndBusiness?: Record<string, unknown>;
}

interface ExportOptions {
  startDate?: Date;
  endDate?: Date;
  businessIdentifier?: string;
  businessName?: string;
  accountCode?: string;
}

type CellValue = string | number;

const STYLES = {
  header: {
    font: { bold: true },
    alignment: { vertical: 'middle', horizontal: 'center' } as const,
    fill: {
      type: 'pattern' as const,
      pattern: 'solid' as const,
      fgColor: { argb: 'FFDDDDDD' },
    },
  },
  columnWidth: 20,
  numberFormat: '#,##0',
} as const;

const HEADERS = [
  'Fecha',
  'RUT',
  'Nombre empresa',
  'Descripción',
  'ID Asiento',
  'Clase Asiento',
  'Documento',
  'Debe',
  'Haber',
] as const;

const MajorBookMapper = {
  toRow(line: MajorBookLine): CellValue[] {
    return [
      formatDate(new Date(line.date), 'dd-mm-yyyy') || '',
      line.businessIdentifier || '',
      line.businessName || '',
      line.description || '',
      line.entryId || '',
      line.className || '',
      line.documentId || '',
      line.debit || 0,
      line.credit || 0,
    ];
  },

  toRows(data: MajorBookResponse): CellValue[][] {
    if (!data?.majorBookAccounts) return [];

    return data.majorBookAccounts.flatMap((category) =>
      category.groupedLines.map((line) => this.toRow(line)),
    );
  },

  calculateTotals(data: MajorBookResponse): {
    totalDebit: number;
    totalCredit: number;
  } {
    if (!data?.majorBookAccounts) {
      return { totalDebit: 0, totalCredit: 0 };
    }

    const totals = data.majorBookAccounts.reduce(
      (acc, category) => {
        const categoryTotals = category.groupedLines.reduce(
          (catAcc, line) => ({
            debit: catAcc.debit + (line.debit || 0),
            credit: catAcc.credit + (line.credit || 0),
          }),
          { debit: 0, credit: 0 },
        );

        return {
          debit: acc.debit + categoryTotals.debit,
          credit: acc.credit + categoryTotals.credit,
        };
      },
      { debit: 0, credit: 0 },
    );

    return {
      totalDebit: totals.debit,
      totalCredit: totals.credit,
    };
  },
};

const ReportInfoMapper = {
  create(options: ExportOptions): (string | undefined)[][] {
    const {
      startDate,
      endDate,
      businessName,
      businessIdentifier,
      accountCode,
    } = options;

    const period =
      startDate && endDate
        ? `${formatDate(startDate, 'dd-mm-yyyy')} a ${formatDate(endDate, 'dd-mm-yyyy')}`
        : `Cuenta: ${accountCode}`;

    return [
      ['Informacion de la consulta', ''],
      ['Razón social:', businessName || 'N/A'],
      ['RUT:', businessIdentifier || 'N/A'],
      ['Tipo de reporte:', 'Libro Mayor'],
      ['Periodo:', period],
      ['Fecha de consulta:', formatDate(new Date(), 'dd-mm-yyyy')],
      [],
    ];
  },
};

class ExcelWorksheetBuilder {
  protected worksheet: ExcelJS.Worksheet;

  constructor(worksheet: ExcelJS.Worksheet) {
    this.worksheet = worksheet;
  }

  addInfoSection(rows: (string | undefined)[][]): this {
    rows.forEach((rowData) => {
      const row = this.worksheet.addRow(rowData);
      if (rowData.length > 0) {
        row.getCell(1).font = { bold: true };
      }
    });
    return this;
  }

  addHeaderRow(headers: readonly string[]): this {
    const row = this.worksheet.addRow([...headers]);
    this.applyHeaderStyle(row);

    this.worksheet.getColumn(8).numFmt = STYLES.numberFormat;
    this.worksheet.getColumn(9).numFmt = STYLES.numberFormat;
    return this;
  }

  addDataRows(rows: CellValue[][]): this {
    rows.forEach((rowData) => this.worksheet.addRow(rowData));
    return this;
  }

  addTotalsRow(totalDebit: number, totalCredit: number): this {
    const totalsRow = this.worksheet.addRow([
      'Totales:',
      '',
      '',
      '',
      '',
      '',
      '',
      totalDebit,
      totalCredit,
    ]);

    totalsRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE6E6E6' },
      };
      cell.border = {
        top: { style: 'double' },
        bottom: { style: 'thin' },
      };
    });

    return this;
  }

  protected applyHeaderStyle(row: ExcelJS.Row): void {
    row.eachCell((cell) => {
      cell.font = STYLES.header.font;
      cell.alignment = STYLES.header.alignment;
      cell.fill = STYLES.header.fill;
      this.worksheet.getColumn(cell.col).width = STYLES.columnWidth;
    });
  }
}

class ExcelDocumentBuilder {
  private workbook: ExcelJS.Workbook;
  private worksheetBuilder: ExcelWorksheetBuilder | null = null;

  constructor() {
    this.workbook = new ExcelJS.Workbook();
  }

  createWorksheet(name: string): ExcelWorksheetBuilder {
    const worksheet = this.workbook.addWorksheet(name);
    this.worksheetBuilder = new ExcelWorksheetBuilder(worksheet);
    return this.worksheetBuilder;
  }

  async toArrayBuffer(): Promise<ArrayBuffer> {
    const buffer = await this.workbook.xlsx.writeBuffer();
    return buffer as ArrayBuffer;
  }
}

const FileDownloader = {
  async downloadExcel(data: ArrayBuffer, fileName: string): Promise<void> {
    const blob = new Blob([data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.click();

    URL.revokeObjectURL(url);
  },
};

const FileNameGenerator = {
  forMajorBook(options: ExportOptions): string {
    const { startDate, endDate, accountCode } = options;

    if (startDate && endDate) {
      return `Libro Mayor ${formatDate(startDate, 'dd-mm-yyyy')} - ${formatDate(endDate, 'dd-mm-yyyy')}.xlsx`;
    }

    return `Libro Mayor ${accountCode}.xlsx`;
  },
};

export const exportMajorBookToExcel = async (
  entries: MajorBookResponse,
  options: ExportOptions,
): Promise<void> => {
  if (!entries?.majorBookAccounts) return;

  const document = new ExcelDocumentBuilder();
  const dataRows = MajorBookMapper.toRows(entries);
  const totals = MajorBookMapper.calculateTotals(entries);

  document
    .createWorksheet('Sheet1')
    .addInfoSection(ReportInfoMapper.create(options))
    .addHeaderRow(HEADERS)
    .addDataRows(dataRows)
    .addTotalsRow(totals.totalDebit, totals.totalCredit);

  const buffer = await document.toArrayBuffer();
  const fileName = FileNameGenerator.forMajorBook(options);

  await FileDownloader.downloadExcel(buffer, fileName);
};

// Interface for account-level major book export (Table component)
interface AccountGroupedLine {
  entryId?: string;
  className?: string;
  date: string;
  description?: string;
  businessIdentifier?: string;
  debit?: number;
  credit?: number;
  debitFinal?: number;
  creditFinal?: number;
  accountName?: string;
  account?: string;
}

interface AccountExportOptions {
  startDate?: Date;
  endDate?: Date;
  businessIdentifier?: string;
  businessName?: string;
  accountCode?: string;
  accountName?: string;
}

// Headers for account-level export (Table component)
const ACCOUNT_HEADERS = [
  'ID Asiento',
  'Clase Asiento',
  'Fecha',
  'Descripción',
  'Rut',
  'Debe',
  'Haber',
] as const;

// Mapper for account-level grouped lines
const AccountMajorBookMapper = {
  toRow(line: AccountGroupedLine): CellValue[] {
    return [
      line.entryId || '',
      line.className || '',
      formatDate(new Date(line.date), 'dd-mm-yyyy') || '',
      line.description || '',
      line.businessIdentifier || '',
      line.debit || 0,
      line.credit || 0,
    ];
  },

  toRows(lines: AccountGroupedLine[]): CellValue[][] {
    return lines.map((line) => this.toRow(line));
  },

  calculateTotals(lines: AccountGroupedLine[]): {
    totalDebit: number;
    totalCredit: number;
  } {
    return lines.reduce(
      (acc, line) => ({
        totalDebit: acc.totalDebit + (line.debit || 0),
        totalCredit: acc.totalCredit + (line.credit || 0),
      }),
      { totalDebit: 0, totalCredit: 0 },
    );
  },
};

// Info mapper for account-level export with separate account fields
const AccountReportInfoMapper = {
  create(options: AccountExportOptions): (string | undefined)[][] {
    const {
      startDate,
      endDate,
      businessName,
      businessIdentifier,
      accountCode,
      accountName,
    } = options;

    const period =
      startDate && endDate
        ? `${formatDate(startDate, 'dd-mm-yyyy')} a ${formatDate(endDate, 'dd-mm-yyyy')}`
        : 'N/A';

    return [
      ['Informacion de la consulta', ''],
      ['Razón social:', businessName || 'N/A'],
      ['RUT:', businessIdentifier || 'N/A'],
      ['Tipo de balance:', 'Libro Mayor'],
      ['Periodo:', period],
      ['Cuenta:', accountCode || 'N/A'],
      ['Nombre de Cuenta:', accountName || 'N/A'],
      ['Fecha de consulta:', formatDate(new Date(), 'dd-mm-yyyy')],
      [],
    ];
  },
};

// Extended worksheet builder for account-level exports
class AccountExcelWorksheetBuilder extends ExcelWorksheetBuilder {
  addTitle(title: string, mergeColumns: number = 7): this {
    const titleRow = this.worksheet.addRow([title]);
    titleRow.font = { bold: true, size: 14 };
    const titleRowNumber = this.worksheet.rowCount;
    this.worksheet.mergeCells(titleRowNumber, 1, titleRowNumber, mergeColumns);
    this.worksheet.addRow([]);
    return this;
  }

  addAccountHeaderRow(): this {
    const row = this.worksheet.addRow([...ACCOUNT_HEADERS]);
    this.applyHeaderStyle(row);

    // Configure column widths
    this.worksheet.getColumn(1).width = 12; // ID Asiento
    this.worksheet.getColumn(2).width = 15; // Clase Asiento
    this.worksheet.getColumn(3).width = 12; // Fecha
    this.worksheet.getColumn(4).width = 40; // Descripción
    this.worksheet.getColumn(5).width = 12; // Rut
    this.worksheet.getColumn(6).width = 15; // Debe
    this.worksheet.getColumn(7).width = 15; // Haber

    // Configure number format for Debe and Haber columns
    this.worksheet.getColumn(6).numFmt = STYLES.numberFormat;
    this.worksheet.getColumn(7).numFmt = STYLES.numberFormat;

    return this;
  }

  addAccountTotalsRow(totalDebit: number, totalCredit: number): this {
    const totalsRow = this.worksheet.addRow([
      'Totales:',
      '',
      '',
      '',
      '',
      totalDebit,
      totalCredit,
    ]);

    totalsRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE6E6E6' },
      };
      cell.border = {
        top: { style: 'double' },
        bottom: { style: 'thin' },
      };
    });

    return this;
  }
}

// Extended document builder for account-level exports
class AccountExcelDocumentBuilder {
  private workbook: ExcelJS.Workbook;
  private worksheetBuilder: AccountExcelWorksheetBuilder | null = null;

  constructor() {
    this.workbook = new ExcelJS.Workbook();
  }

  createWorksheet(name: string): AccountExcelWorksheetBuilder {
    const worksheet = this.workbook.addWorksheet(name);
    this.worksheetBuilder = new AccountExcelWorksheetBuilder(worksheet);
    return this.worksheetBuilder;
  }

  async toArrayBuffer(): Promise<ArrayBuffer> {
    const buffer = await this.workbook.xlsx.writeBuffer();
    return buffer as ArrayBuffer;
  }
}

const AccountFileNameGenerator = {
  forAccountMajorBook(accountName: string): string {
    const sanitizedName = accountName.replace(/\s+/g, '_');
    const dateStr = new Date().toISOString().split('T')[0];
    return `libro_mayor_${sanitizedName}_${dateStr}.xlsx`;
  },
};

export const exportAccountMajorBookToExcel = async (
  groupedLines: AccountGroupedLine[],
  options: AccountExportOptions,
): Promise<void> => {
  if (!groupedLines || groupedLines.length === 0) return;

  const document = new AccountExcelDocumentBuilder();
  const dataRows = AccountMajorBookMapper.toRows(groupedLines);
  const totals = AccountMajorBookMapper.calculateTotals(groupedLines);

  const accountName =
    options.accountName || groupedLines[0]?.accountName || 'Cuenta';

  document
    .createWorksheet('Libro Mayor')
    .addInfoSection(AccountReportInfoMapper.create(options))
    .addAccountHeaderRow()
    .addDataRows(dataRows)
    .addAccountTotalsRow(totals.totalDebit, totals.totalCredit);

  const buffer = await document.toArrayBuffer();
  const fileName = AccountFileNameGenerator.forAccountMajorBook(accountName);

  await FileDownloader.downloadExcel(buffer, fileName);
};
